/**
 * TEMPORARY diagnostics for the Task Ad quest (41) — the "GO" widget that can
 * navigate to another Mini App or a plain bot chat.
 *
 * Added 2026-08-26 after two shipped fixes still left the reward ungranted on
 * return. Static reading of AdsGram's `<adsgram-task>` web component stopped
 * being useful — it's a black box behind shadow DOM we don't control — so this
 * records what actually happens in the player's session (every click reaching
 * us, every custom event the widget fires, every shadow-DOM slot change, every
 * step of our own reward-unlock logic) and ships it to the backend, where it
 * lands in the container log as TASKADLOG.
 *
 * Uses `fetch(..., { keepalive: true })` directly rather than the generated API
 * client: the whole point is to survive the exact moment the page may be about
 * to navigate away or unload, which is also why every call site should call
 * taskAdLogFlushNow() right after logging anything that precedes a possible
 * navigation, instead of waiting for the debounce.
 *
 * DELETE THIS FILE and its call sites once the bug is closed.
 */

import { getApiBase, getCommonHeaders } from '../net/apiRequestHeaders';

const buffer: Array<{ t: number; ev: string; [k: string]: unknown }> = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const startedAt = Date.now();

function flush(): void {
  flushTimer = null;
  if (buffer.length === 0) return;
  const entries = buffer.splice(0, buffer.length);
  try {
    const headers = new Headers(getCommonHeaders());
    headers.set('Content-Type', 'application/json');
    void fetch(`${getApiBase()}/debug/taskad-log`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ entries }),
      keepalive: true,
    }).catch(() => {
      // Diagnostics must never break the game.
    });
  } catch {
    // Diagnostics must never break the game.
  }
}

function schedule(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, 800);
}

export function taskAdLog(ev: string, data: Record<string, unknown> = {}): void {
  buffer.push({ t: Date.now() - startedAt, ev, ...data });
  if (buffer.length > 500) buffer.splice(0, buffer.length - 500);
  console.log('[TASKAD]', ev, data);
  schedule();
}

/** Flush immediately — call right before/after anything that might navigate away or unload the page. */
export function taskAdLogFlushNow(): void {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  flush();
}
